import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'
import type {
  RemoveSubIssueInput,
  removeSubIssueMutation,
  removeSubIssueMutation$data,
} from './__generated__/removeSubIssueMutation.graphql'

function getSubIssuesConnection(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssues', {first: 100}) || null
}

function getSubIssuesConnectionAlias(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssuesConnection') || null
}

function optimisticRemoveSubIssueFromConnection(store: RecordSourceProxy, input: RemoveSubIssueInput) {
  const subIssueRecord = store.get(input.subIssueId)

  const parentRecord = store.get(input.issueId)
  if (parentRecord) {
    const subIssuesConnection = getSubIssuesConnection(parentRecord)

    if (subIssuesConnection) {
      const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
      const newNodes = currentNodes.filter(node => node?.getValue('id') !== input.subIssueId)

      if (newNodes.length !== currentNodes.length) {
        subIssuesConnection.setLinkedRecords(newNodes, 'nodes')

        const currentTotalCount = subIssuesConnection.getValue('totalCount')
        if (typeof currentTotalCount === 'number') {
          subIssuesConnection.setValue(Math.max(0, currentTotalCount - 1), 'totalCount')
        }
      }

      // Update subIssuesSummary on the parent if it exists
      const subIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
      if (subIssuesSummary) {
        const currentTotal = subIssuesSummary.getValue('total')
        if (typeof currentTotal === 'number') {
          subIssuesSummary.setValue(Math.max(0, currentTotal - 1), 'total')
        }

        // Update completed count if the sub-issue is closed
        if (subIssueRecord) {
          const isClosed = subIssueRecord.getValue('closed') === true
          if (isClosed) {
            const currentCompleted = subIssuesSummary.getValue('completed')
            if (typeof currentCompleted === 'number') {
              subIssuesSummary.setValue(Math.max(0, currentCompleted - 1), 'completed')
            }
          }
        }
      }

      // Update subIssuesConnection totalCount (aliased field used by useSubIssuesSummary)
      const subIssuesConnectionRecord = getSubIssuesConnectionAlias(parentRecord)
      if (subIssuesConnectionRecord) {
        const connectionTotalCount = subIssuesConnectionRecord.getValue('totalCount')
        if (typeof connectionTotalCount === 'number') {
          subIssuesConnectionRecord.setValue(Math.max(0, connectionTotalCount - 1), 'totalCount')
        }
      }
    }
  }

  // Always clear the parent field on the sub-issue so the RelationshipsSection updates immediately
  if (subIssueRecord) {
    subIssueRecord.setValue(null, 'parent')
  }
}

export function commitRemoveSubIssueMutation({
  environment,
  input,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: RemoveSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: removeSubIssueMutation$data) => void
}) {
  return commitMutation<removeSubIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation removeSubIssueMutation($input: RemoveSubIssueInput!) @raw_response_type {
        removeSubIssue(input: $input) {
          issue {
            ... on Issue {
              id
              state
              stateReason
              issueDependenciesSummary {
                blockedBy
              }
              assignees(first: 10) {
                totalCount
                edges {
                  node {
                    id
                    login
                    avatarUrl
                  }
                }
              }
              url
              repository {
                name
                owner {
                  login
                }
              }
              databaseId
              number
              title
              titleHTML
              issueType {
                id
                name
                color
              }
              subIssuesSummary {
                total
                completed
              }
              closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
                totalCount
              }
              subIssues(first: 100) {
                nodes {
                  id
                }
              }
            }
          }
          subIssue {
            id
            parent {
              id
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: store => optimisticRemoveSubIssueFromConnection(store, input),
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}
